import { FC, useState } from 'react';
import styles from './ProviderMarks.module.css';

import equifaxLogo from './assets/equifax.svg';
import trueworkLogo from './assets/truework.svg';
import truvLogo from './assets/truv.svg';
import argyleLogo from './assets/argyle.svg';
import halcyonLogo from './assets/halcyon.png';
import plaidLogo from './assets/plaid.svg';
import experianLogo from './assets/experian.svg';

/**
 * Feature-local vendor -> approved logo asset map. Vendors not listed here (any unknown vendor)
 * fall back to a readable monogram tile, as does any vendor whose logo image fails to load.
 * Assets are bundled locally; no third-party CDN.
 */
const VENDOR_LOGOS: Record<string, string> = {
  Equifax: equifaxLogo,
  Truework: trueworkLogo,
  Truv: truvLogo,
  Argyle: argyleLogo,
  Halcyon: halcyonLogo,
  Plaid: plaidLogo,
  Experian: experianLogo,
};

const MONOGRAM_COLORS: Record<string, string> = {
  Experian: '#1565c0',
  Plaid: '#00695c',
};

const monogramFor = (vendor: string): string => vendor.substring(0, 2).toUpperCase();

/**
 * `showName` adds the vendor name beside the mark, but only in the monogram fallback:
 * an approved logo already contains the full brand name, so the redundant text is dropped
 * and the name is exposed as a hover tooltip / accessible alt instead.
 */
export const ProviderMark: FC<{ vendor: string; showName?: boolean; nameClassName?: string }> = ({
  vendor,
  showName = false,
  nameClassName,
}) => {
  // A failed logo load switches to the monogram once and never retries the broken image.
  const [logoFailed, setLogoFailed] = useState(false);
  const logo = VENDOR_LOGOS[vendor];

  if (logo && !logoFailed) {
    return (
      <img
        className={styles.logo}
        src={logo}
        alt={`${vendor} logo`}
        title={vendor}
        onError={() => setLogoFailed(true)}
      />
    );
  }

  return (
    <>
      <span className={styles.mark} style={{ backgroundColor: MONOGRAM_COLORS[vendor] ?? '#78909c' }} role="img" aria-label={vendor}>
        {monogramFor(vendor)}
      </span>
      {showName ? <span className={nameClassName}>{vendor}</span> : null}
    </>
  );
};
